import Layout from 'components/Layout';
import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Route, Routes } from 'react-router-dom';
import GetStartedPage from './pages/GetStartedPage';
import PostPage from './pages/PostPage';
import { ISocialMediaContext } from './types';
import {
  ISocailMediaFormData,
  SocialMediaContext,
  SocialMediaContextType,
  PlatformConnection
} from './SocialMediaContext';
import { SocialMediaResponse } from './services/socialMediaCommonApi';
import remarkHtml from 'remark-html';
import remarkMdx from 'remark-mdx';
import remarkParse from 'remark-parse';
import { unified } from 'unified';
import SocialMediaResponsePage from './pages/ResponsePage';

interface Connections {
  linkedin: PlatformConnection;
  x: PlatformConnection;
}

const defaultConnections: Connections = {
  linkedin: { isConnected: false },
  x: { isConnected: false }
};

// Type guard to check if the parsed data matches our Connections interface
const isValidConnections = (data: unknown): data is Connections => {
  if (!data || typeof data !== 'object') return false;
  
  const connections = data as Record<string, unknown>;
  
  return (
    'linkedin' in connections &&
    'x' in connections &&
    typeof connections.linkedin === 'object' &&
    typeof connections.x === 'object' &&
    connections.linkedin !== null &&
    connections.x !== null &&
    'isConnected' in connections.linkedin &&
    'isConnected' in connections.x &&
    typeof (connections.linkedin as any).isConnected === 'boolean' &&
    typeof (connections.x as any).isConnected === 'boolean'
  );
};

const SocialMedia = () => {
  const [isSettingSocialMedia, setIsSettingSocialMedia] = useState(false);
  const [socialMediaContent, setSocialMediaContent] = useState<ISocialMediaContext[]>([]);
  const [isEditing, setisEditing] = useState(false);
  const [connections, setConnections] = useState<Connections>(defaultConnections);

  const [formData, setFormData] = useState<ISocailMediaFormData>({
    description: '',
    drafts: 0,
    imageType: 'generate',
    language: '',
    subTopic: '',
    tone: '',
    topic: '',
  });

  // Load connection status from localStorage on mount with type checking
  useEffect(() => {
    const savedConnections = localStorage.getItem('socialMediaConnections');
    if (savedConnections) {
      try {
        const parsed = JSON.parse(savedConnections);
        if (isValidConnections(parsed)) {
          setConnections(parsed);
        } else {
          console.error('Invalid connections data in localStorage');
          setConnections(defaultConnections);
        }
      } catch (error) {
        console.error('Error parsing saved connections:', error);
        setConnections(defaultConnections);
      }
    }
  }, []);

  // Save connection status to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('socialMediaConnections', JSON.stringify(connections));
  }, [connections]);

  const handleFormDataUpdate = (e: ISocailMediaFormData) => setFormData(e);

  const handleUpdateSocialMediaContent = async (payload: SocialMediaResponse[]) => {
    try {
      const data = await Promise.all(
        payload.map(async ({ image_url, post }) => {
          const content = await unified()
            .use(remarkParse)
            .use(remarkMdx)
            .use(remarkHtml)
            .process(post);

          setIsSettingSocialMedia(true);

          return {
            content: content.toString(),
            markdownContent: post,
            image: image_url,
          };
        })
      );

      setSocialMediaContent(data);
    } catch (error) {
      console.error('Error updating blog content:', error);
      toast.error('Failed to update blog content');
    } finally {
      setIsSettingSocialMedia(false);
    }
  };

  const handleUpdateEditing = (e: boolean) => setisEditing(e);
  
  const updateSocialMediaContent = (data: ISocialMediaContext[]) => {
    setSocialMediaContent(data);
  };

  // Platform connection management methods
  const connectPlatform = (platform: 'linkedin' | 'x', connectionData: PlatformConnection) => {
    setConnections(prev => ({
      ...prev,
      [platform]: {
        ...connectionData,
        isConnected: true,
        lastConnected: new Date()
      }
    }));
    toast.success(`Connected to ${platform === 'x' ? 'X' : 'LinkedIn'} successfully`);
  };

  const disconnectPlatform = (platform: 'linkedin' | 'x') => {
    setConnections(prev => ({
      ...prev,
      [platform]: { isConnected: false }
    }));
    toast.success(`Disconnected from ${platform === 'x' ? 'X' : 'LinkedIn'}`);
  };

  const isPlatformConnected = (platform: 'linkedin' | 'x'): boolean => {
    return connections[platform].isConnected;
  };

  const getPlatformUsername = (platform: 'linkedin' | 'x'): string | undefined => {
    const connection = connections[platform];
    return connection.username;
  };

  const value: SocialMediaContextType = {
    socialMediaContent,
    isEditing,
    isSettingSocialMedia,
    formData,
    connections,
    setIsEditing: handleUpdateEditing,
    handleUpdateSocialMediaContent,
    updateFormData: handleFormDataUpdate,
    updateSocialMediaContent,
    connectPlatform,
    disconnectPlatform,
    isPlatformConnected,
    getPlatformUsername,
  };

  return (
    <SocialMediaContext.Provider value={value}>
      <Layout>
        <Routes>
          <Route path="/get-started" element={<GetStartedPage />} />
          <Route path="/post" element={<PostPage />} />
          <Route path="/response" element={<SocialMediaResponsePage />} />
        </Routes>
      </Layout>
    </SocialMediaContext.Provider>
  );
};

export default SocialMedia;